/**
 * Контекст анализа — один на всё приложение и адресуемый в URL.
 *
 * Экраны не изобретают собственных параметров чтения: дата состояния и
 * «включать ли демо» живут здесь, попадают в каждый запрос и возвращаются
 * сервером обратно. Расхождение между тем, что показано, и тем, что применено,
 * становится видимым, а не подразумеваемым.
 *
 * СРЕЗ И ФИЛЬТР ВИДА — РАЗНЫЕ ВЕЩИ, и здесь они разделены полями.
 *
 * * **срез** (`asOf`, `includeDemo`) отвечает на вопрос «что вообще считалось
 *   существующим»: он выбирает версии утверждений и решает, участвует ли
 *   синтетика. Он едет с каждым переходом (`linkSearch`), потому что иначе
 *   спуск на уровень ниже молча менял бы мир;
 * * **фильтр вида** (`view`: сезон, массив, хозяйство) корпус не трогает — он
 *   выбирает ЧАСТЬ уже существующего корпуса и тем самым меняет ЗНАМЕНАТЕЛЬ:
 *   доля считается от отфильтрованной популяции. С переходом он НЕ едет, и это
 *   то же правило, что для любого локального фильтра: «сезон 2025» на экране
 *   агроцикла и на обзоре района означает разные выборки, и перенос молча
 *   подменил бы одну другой.
 *
 * Из разделения следует запрет, ради которого оно и объявлено: фильтр вида
 * никогда не подставляется в `asOf`. «Сезон 2025» — это не «состояние на 2025
 * год»: у прошлого сезона есть отчёты, внесённые позже его окна.
 */

import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import type { QueryParams } from "../api/client";

/**
 * Оси фильтра ВИДА. `null` — ось не задана, то есть выборка не сужена; это не
 * «сузили до пустого» и различать обязательно.
 */
export interface ViewFilters {
  /** Метка сезона: строка, а не год — сезон бывает переходным («2025-2026»). */
  season: string | null;
  massifId: number | null;
  actorId: number | null;
}

/**
 * Имена осей вида В АДРЕСНОЙ СТРОКЕ — намеренно НЕ совпадающие с именами
 * серверных параметров.
 *
 * Ось вида глобальна: она живёт в адресе любого экрана и уезжает в запрос
 * везде, где её читают. Локальный фильтр экрана — нет: `massif_id` на очереди
 * проверки и `actor_id` в реестре сужают ОДИН экран и на чужом означают другое.
 * Совпади имена — первый же экран, который держит свой `massif_id` и при этом
 * шлёт `viewParams`, молча сузил бы серверную выборку своим локальным
 * фильтром, и ошибка выглядела бы как «данных меньше, чем должно быть».
 *
 * Поэтому в адресе ось вида носит приставку `view_`, а в запрос уходит под
 * серверным именем (:const:`VIEW_QUERY`). Сезон приставки не требует:
 * локального фильтра `season` ни на одном экране нет, и ось вида — его
 * единственное значение.
 */
export const VIEW_PARAMS = {
  season: "season",
  massifId: "view_massif_id",
  actorId: "view_actor_id",
} as const;

/** Имена тех же осей в запросе к серверу. Это контракт API, а не адреса. */
export const VIEW_QUERY = {
  season: "season",
  massifId: "massif_id",
  actorId: "actor_id",
} as const;

export interface AnalysisContextValue {
  asOf: string | null;
  includeDemo: boolean;
  /** То, что уходит в запрос. Ключ кэша хуков — этот же объект. */
  params: QueryParams;
  /**
   * Фильтры вида, разобранные из адреса. НЕ часть среза: см. заголовок модуля.
   */
  view: ViewFilters;
  /**
   * Срез ПЛЮС заданные оси вида — для выдач, которые фильтр вида принимают.
   * Отдельно от `params`, потому что принимают его не все: послать `season`
   * туда, где его не читают, значило бы решить, что выборка сужена.
   */
  viewParams: QueryParams;
  /** Стабильный ключ контекста для зависимостей эффектов. */
  key: string;
  /** Ключ контекста ВМЕСТЕ с осями вида — для хуков, читающих `viewParams`. */
  viewKey: string;
  /**
   * Query-строка ТОЛЬКО с контекстом анализа (без фильтров конкретного вида).
   *
   * Ею адресуются переходы между разными экранами: локальный фильтр одного
   * экрана (например, номер контура в `q`) не должен приезжать на чужой экран
   * и молча превращаться там в другой фильтр. Оси вида сюда не входят по той же
   * причине.
   */
  linkSearch: (extra?: Record<string, string | number>) => string;
  setAsOf: (value: string | null) => void;
  setIncludeDemo: (value: boolean) => void;
  /** Задать одну ось вида. `null` — снять ось (вернуться к несуженной выборке). */
  setViewFilter: (axis: keyof ViewFilters, value: string | number | null) => void;
}

const AnalysisContext = createContext<AnalysisContextValue | null>(null);

/**
 * Производные поля контекста по срезу и осям вида — ОДНО место, где из них
 * собираются параметры запроса, ключи и адресная строка. Им пользуются и
 * провайдер адреса, и `AnalysisSliceScope`: второй сборщик рядом разошёлся бы
 * с первым молча (запрос ушёл бы с одним срезом, а ссылка — с другим).
 */
function derive(asOf: string | null, includeDemo: boolean, view: ViewFilters) {
  const params: QueryParams = {};
  if (asOf) params.as_of = asOf;
  if (includeDemo) params.include_demo = true;
  const viewParams: QueryParams = { ...params };
  if (view.season !== null) viewParams[VIEW_QUERY.season] = view.season;
  if (view.massifId !== null) viewParams[VIEW_QUERY.massifId] = view.massifId;
  if (view.actorId !== null) viewParams[VIEW_QUERY.actorId] = view.actorId;
  return {
    params,
    viewParams,
    key: `${asOf ?? ""}|${includeDemo}`,
    viewKey: `${asOf ?? ""}|${includeDemo}|${view.season ?? ""}|${view.massifId ?? ""}|${view.actorId ?? ""}`,
    linkSearch: (extra?: Record<string, string | number>) => {
      const query = new URLSearchParams();
      if (asOf) query.set("as_of", asOf);
      if (!includeDemo) query.set("include_demo", "false");
      for (const [key, value] of Object.entries(extra ?? {})) {
        query.set(key, String(value));
      }
      const text = query.toString();
      return text ? `?${text}` : "";
    },
  };
}

const NO_VIEW: ViewFilters = { season: null, massifId: null, actorId: null };

export function AnalysisContextProvider({ children }: { children: ReactNode }) {
  const [search, setSearch] = useSearchParams();
  const asOf = search.get("as_of");
  // Демо по умолчанию ВКЛЮЧЕНО: стенд целиком синтетический, и выключенный
  // по умолчанию флаг показывал бы пустой район. Выключение — явное
  // `include_demo=false` в адресе; флаг при этом соблюдается ВСЕМИ экранами
  // (кабинет решений, карточки, кабинеты, очередь проверки, записи) — ни один
  // адаптер его не подменяет.
  const includeDemo = search.get("include_demo") !== "false";
  const season = search.get(VIEW_PARAMS.season);
  const massifParam = search.get(VIEW_PARAMS.massifId);
  const actorParam = search.get(VIEW_PARAMS.actorId);

  const value = useMemo<AnalysisContextValue>(() => {
    // Число или `null`: «massif_id=abc» — не ось, а мусор в адресе, и
    // подставлять вместо него NaN значило бы отправить на сервер вопрос,
    // которого никто не задавал.
    const asNumber = (raw: string | null): number | null => {
      if (raw === null || raw.trim() === "") return null;
      const parsed = Number(raw);
      return Number.isInteger(parsed) ? parsed : null;
    };
    const view: ViewFilters = {
      season: season && season.trim() !== "" ? season : null,
      massifId: asNumber(massifParam),
      actorId: asNumber(actorParam),
    };

    const update = (patch: Record<string, string | null>) => {
      setSearch(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [key, item] of Object.entries(patch)) {
            if (item === null) next.delete(key);
            else next.set(key, item);
          }
          return next;
        },
        { replace: true },
      );
    };

    return {
      asOf,
      includeDemo,
      view,
      ...derive(asOf, includeDemo, view),
      setAsOf: (next) => update({ as_of: next }),
      setIncludeDemo: (next) => update({ include_demo: next ? null : "false" }),
      setViewFilter: (axis, next) =>
        update({
          [VIEW_PARAMS[axis]]: next === null ? null : String(next),
        }),
    };
  }, [asOf, includeDemo, season, massifParam, actorParam, setSearch]);

  return (
    <AnalysisContext.Provider value={value}>{children}</AnalysisContext.Provider>
  );
}

export function useAnalysisContext(): AnalysisContextValue {
  const value = useContext(AnalysisContext);
  if (!value) {
    throw new Error("useAnalysisContext вызван вне AnalysisContextProvider");
  }
  return value;
}

/**
 * Срез, названный СОДЕРЖИМЫМ, а не адресом: отчёт ИИ собран «на 20.09, без
 * демо», и его живые виджеты читают именно этот срез, где бы отчёт ни
 * показывали — в панели поверх любого экрана, на холсте, на странице по
 * ссылке (адрес которой срез не несёт вовсе).
 *
 * * Срез задан пропсами; внутри он ЛОКАЛЕН: ползунок даты виджета двигает
 *   только виджет и не пишет в адрес хозяина — иначе страница отчёта
 *   перечитала бы чужой срез, а ползунок «не работал» бы.
 * * Оси вида (сезон, массив, хозяйство) из адреса хозяина сюда НЕ попадают:
 *   виджет назван своими параметрами (цикл, хозяйство), и фильтр экрана под
 *   ним молча сузил бы выборку (знаменатель) и разошёлся с числами ответа.
 * * Ссылки внутри виджета (`linkSearch`) несут этот же срез: переход ведёт на
 *   экран в срезе отчёта, а не в текущем срезе адреса.
 *
 * Смена среза пропсами (другая версия отчёта) пересоздаёт область: локальные
 * правки даты не переживают её.
 */
export function AnalysisSliceScope(props: { asOf: string | null; includeDemo: boolean; children: ReactNode }) {
  return <SliceScope key={`${props.asOf ?? ""}|${props.includeDemo}`} {...props} />;
}

function SliceScope({ asOf: initialAsOf, includeDemo: initialDemo, children }: { asOf: string | null; includeDemo: boolean; children: ReactNode }) {
  const [asOf, setAsOf] = useState<string | null>(initialAsOf);
  const [includeDemo, setIncludeDemo] = useState(initialDemo);
  const value = useMemo<AnalysisContextValue>(
    () => ({
      asOf,
      includeDemo,
      view: NO_VIEW,
      ...derive(asOf, includeDemo, NO_VIEW),
      setAsOf,
      setIncludeDemo,
      // Оси вида внутри области не живут: задавать нечего.
      setViewFilter: () => undefined,
    }),
    [asOf, includeDemo],
  );
  return <AnalysisContext.Provider value={value}>{children}</AnalysisContext.Provider>;
}
